/**
 * Sidebar Navigation Component
 * Fixed left sidebar with role-based navigation items
 */

import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getNavByRole, getRoleSubtitle, NavItem } from './navConfig';
import { getTicketsSummary, TicketsSummary } from '../services/ticketsApi';
import * as Icons from './icons';
import logoImage from '../assets/logo/Container.png';
import { VersionBadge } from '../components/VersionBadge';
import styles from './Sidebar.module.css';

export const Sidebar: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navConfig = useMemo(() => getNavByRole(user?.role || 'default'), [user?.role]);

  // Call Center sidebar shows live ticket counts. We fetch the summary once
  // on mount and refresh every 60s while the sidebar is mounted, then map
  // each nav item's `badgeKey` / `badgeCategory` to a value off the response.
  // Other roles skip this entirely so we don't hit the endpoint unnecessarily.
  const isCallCenter = (user?.role || '').toLowerCase().replace('_', ' ') === 'call center';
  const [ticketsSummary, setTicketsSummary] = useState<TicketsSummary | null>(null);
  useEffect(() => {
    if (!isCallCenter) return;
    let cancelled = false;
    const refresh = () => {
      getTicketsSummary()
        .then((s) => { if (!cancelled) setTicketsSummary(s); })
        .catch(() => { /* leave badges empty on error rather than crash */ });
    };
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isCallCenter]);

  /** Resolve a badge value for an item, prefering live data over static. */
  const getBadgeValue = (item: NavItem): number | undefined => {
    if (ticketsSummary) {
      if (item.badgeKey) return ticketsSummary[item.badgeKey];
      if (item.badgeCategory) return ticketsSummary.by_category?.[item.badgeCategory] ?? 0;
    }
    return item.badge;
  };

  // Dynamically import icon component based on string key
  const getIconComponent = (iconKey: string | undefined) => {
    if (!iconKey) return null;
    const IconComponent = Icons[iconKey as keyof typeof Icons];
    return IconComponent ? <IconComponent /> : null;
  };

  return (
    <aside className={styles.sidebar}>
      {/* Logo Block */}
      <div className={styles.logoBlock}>
        <div className={styles.logoHeader}>
          <img src={logoImage} alt="VIN Pulse OS" className={styles.logo} />
          <div className={styles.textColumn}>
            <div className={styles.appTitle}>VIN pulse OS</div>
            <div className={styles.roleSubtitle}>{getRoleSubtitle(user?.role || 'default')}</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        {navConfig.map((section, sectionIndex) => {
          const isQuickActions = section.section === 'Quick Actions';

          if (isQuickActions) {
            return (
              <div key={sectionIndex} className={styles.quickActionsContainer}>
                <div className={styles.quickActionsTitle}>{section.section}</div>
                <div className={styles.quickActionsBox}>
                  {section.items.map((item, index) => {
                    const isPrimary = index === 0;
                    return (
                      <button
                        key={item.id}
                        className={`${styles.quickActionBtn} ${
                          isPrimary ? styles.quickActionBtnPrimary : styles.quickActionBtnSecondary
                        }`}
                        onClick={() => {
                          if (!item.path?.startsWith('#')) {
                            window.location.pathname = item.path;
                          }
                        }}
                        title={item.label}
                      >
                        <span className={styles.quickActionIcon}>{getIconComponent(item.icon)}</span>
                        <span className={styles.quickActionLabel}>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }

          return (
            <div key={sectionIndex} className={styles.navSection}>
              {section.section && <div className={styles.sectionHeader}>{section.section}</div>}
              <ul className={styles.navList}>
                {section.items.map((item) => {
                  const badge = getBadgeValue(item);
                  return (
                    <li key={item.id} className={styles.navItem}>
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          `${styles.navLink} ${isActive ? styles.active : ''}`
                        }
                        title={item.label}
                      >
                        <span className={styles.navIcon}>{getIconComponent(item.icon)}</span>
                        <span className={styles.navLabel}>{item.label}</span>
                        {badge !== undefined && badge > 0 && (
                          <span className={styles.badge}>{badge}</span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Footer (optional - can be used for user info or additional actions) */}
      <div className={styles.sidebarFooter}>
        {user && (
          <div className={styles.userInitial}>
            {user.full_name.charAt(0).toUpperCase()}
          </div>
        )}
        <VersionBadge />
      </div>
    </aside>
  );
};
